"use client";

import React from "react";
import { ShieldCheck, CalendarClock, Users, HeartHandshake, ArrowRight } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const valuesData = [{
  title: "Pension Support",
  description: "We support long-term stability through pension contributions that help employees plan beyond the work in front of them.",
  icon: ShieldCheck
}, {
  title: "Structured Flexibility",
  description: "Where the work allows, we provide flexibility so people can manage professional responsibilities alongside life outside the office.",
  icon: CalendarClock
}, {
  title: "Manageable Workloads",
  description: "We plan work carefully, set clear priorities, and avoid building a culture around constant overtime or last-minute pressure.",
  icon: Users
}, {
  title: "Mutual Respect",
  description: "We respect people’s time, judgment, and contribution. A healthy workplace depends on trust, communication, and clear expectations.",
  icon: HeartHandshake
}];
export default function CoreValues() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: cardsRef,
    isVisible: areCardsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: footerRef,
    isVisible: isFooterVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="259121" id="wellness-and-benefits-that-make-sense" className="bg-white text-foreground py-16 md:py-24 overflow-hidden border-b border-border/40">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-12 md:mb-16 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="relative rounded-3xl overflow-hidden shadow-xl border border-border/60 bg-white p-3">
            <img src="https://stable-woodcock.10web.cloud/wp-content/uploads/2026/05/ChatGPT-Image-May-16-2026-09_26_36-AM.png" alt="RiverBend team members in a calm professional setting, reflecting a balanced and supportive workplace" className="object-cover w-full aspect-[4/3] lg:aspect-[5/4] rounded-2xl" loading="lazy" />

            <div className="absolute inset-3 rounded-2xl bg-gradient-to-t from-primary-900/35 via-transparent to-transparent pointer-events-none" />

            <div className="absolute left-6 bottom-6 bg-white/95 rounded-2xl px-5 py-4 shadow-lg border border-white/80 max-w-[280px]">
              <p className="font-default font-semibold text-primary-600 text-sm mb-1">
                Built for sustainable work
              </p>

              <p className="font-default text-xs text-muted-foreground leading-relaxed">
                Benefits and workplace practices should support real people,
                not just fill a policy page.
              </p>
            </div>
          </div>

          <div className="flex flex-col justify-center max-w-2xl lg:ml-auto">
            <div className="h-1 w-12 bg-secondary-500 mb-6 rounded-full" />

            <p className="text-secondary-700 font-semibold tracking-[0.18em] text-xs uppercase mb-4 font-default">
              Wellness and Benefits
            </p>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-primary-600 leading-tight tracking-tight">
              Benefits that make sense.
            </h2>

            <div className="space-y-5 text-muted-foreground font-default text-lg leading-relaxed mt-5">
              <p>
                At RiverBend, wellness is connected to how work is planned,
                managed, and supported. We want people to have stability, clear
                expectations, and the ability to do good work without burning
                out.
              </p>

              <p>
                Our approach is practical: reasonable workloads, respectful
                communication, pension support, and flexibility where it fits
                the work and the needs of our clients.
              </p>
            </div>
          </div>
        </div>

        <div ref={cardsRef} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6 mb-12 md:mb-16 transition-all duration-1000 delay-200 ease-out ${areCardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {valuesData.map((value, index) => {
          const Icon = value.icon;
          return <Card key={index} data-index={index} className="bg-slate-50 border border-border/70 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300 h-full">
                <CardHeader className="p-6 pb-4">
                  <div className="w-12 h-12 rounded-xl bg-secondary-100 flex items-center justify-center mb-5 text-secondary-700">
                    <Icon className="w-6 h-6" />
                  </div>

                  <CardTitle className="text-xl font-default font-semibold text-primary-600 leading-snug">
                    {value.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="px-6 pb-6 pt-0">
                  <CardDescription className="text-sm md:text-base font-default text-muted-foreground leading-relaxed">
                    {value.description}
                  </CardDescription>
                </CardContent>
              </Card>;
        })}
        </div>

        <div ref={footerRef} className={`max-w-3xl mx-auto text-center transition-all duration-1000 delay-400 ease-out ${isFooterVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-lg md:text-xl font-default text-muted-foreground leading-relaxed">
            We are building a workplace where people feel respected, supported,
            and able to do meaningful work without sacrificing their quality of
            life.
          </p>

          <div className="flex justify-center mt-8">
            <Button asChild size="lg" className="rounded-md px-8 bg-primary-600 hover:bg-primary-700 text-primary-foreground shadow-sm">
              <Link to="/TeamAndCareersPage">
                Join Our Team
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}